/**
 * Federation service — Marketplace Federation lifecycle.
 *
 * Backend contract is documented in docs/federation-backend.md. Until the
 * backend ships these endpoints, every call here will 404 — the frontend
 * still type-checks and lays out the pages so we can review them before
 * wiring goes live.
 */
import api from './apiClient';
import type {
  Federation,
  FederationScope,
  FederationState,
  FederationAuditEvent,
} from '../types/federation';

export type FederationDirection = 'outbound' | 'inbound' | 'all';

export interface ListFederationsArgs {
  /** outbound = where I'm the originator; inbound = where I'm the receiver. */
  direction?: FederationDirection;
  state?: FederationState;
  /** When set, only federations touching this counterparty. */
  counterpartyTenantId?: string;
}

export async function listFederations(args: ListFederationsArgs = {}): Promise<Federation[]> {
  const q = new URLSearchParams();
  if (args.direction) q.append('direction', args.direction);
  if (args.state) q.append('state', args.state);
  if (args.counterpartyTenantId) q.append('counterpartyTenantId', args.counterpartyTenantId);
  const qs = q.toString();
  return api.get<Federation[]>(`/federations${qs ? `?${qs}` : ''}`);
}

export async function getFederation(id: string): Promise<Federation> {
  return api.get<Federation>(`/federations/${encodeURIComponent(id)}`);
}

export interface ProposeFederationInput {
  toTenantId: string;
  scope: FederationScope;
}

/** Service Provider Request — Consulting Firm proposes scope to a Client. */
export async function proposeFederation(input: ProposeFederationInput): Promise<Federation> {
  return api.post<Federation>('/federations', input);
}

/** Client accepts an SPR — issues the federation key, transitions to accepted. */
export async function acceptFederation(id: string): Promise<Federation> {
  return api.post<Federation>(`/federations/${encodeURIComponent(id)}/accept`);
}

/** Client rejects an SPR — terminal state. */
export async function rejectFederation(id: string, reason?: string): Promise<Federation> {
  return api.post<Federation>(`/federations/${encodeURIComponent(id)}/reject`, { reason });
}

/** Either party renews an active federation — new duration, scope optionally updated. */
export async function renewFederation(
  id: string,
  patch: { durationMonths?: number; scope?: Partial<FederationScope> },
): Promise<Federation> {
  return api.post<Federation>(`/federations/${encodeURIComponent(id)}/renew`, patch);
}

/**
 * Either party revokes — freezes federated artefacts immediately and
 * schedules the 7-day cleanup job. The federation moves to `revoked` here
 * and reaches `cleaned_up` once the cleanup completes.
 */
export async function revokeFederation(id: string, reason?: string): Promise<Federation> {
  return api.post<Federation>(`/federations/${encodeURIComponent(id)}/revoke`, { reason });
}

/** Mark cleanup complete — backend-internal in production; exposed for ops. */
export async function completeCleanup(id: string): Promise<Federation> {
  return api.post<Federation>(`/federations/${encodeURIComponent(id)}/complete-cleanup`);
}

/** Audit log for a federation — used by the FederationDetail page. */
export async function getFederationAuditLog(id: string): Promise<FederationAuditEvent[]> {
  return api.get<FederationAuditEvent[]>(`/federations/${encodeURIComponent(id)}/audit-log`);
}

/**
 * Marketplace browse — list Root Entities that can be federated with. The
 * backend filters by archetype on the caller's side (a consulting_firm
 * browsing only sees client tenants, etc.).
 */
export interface MarketplaceEntry {
  tenantId: string;
  name: string;
  slug: string;
  rootEntityType: string | null;
  industry: string | null;
  /** Country / region for sovereignty matching. */
  country: string | null;
  /** Short blurb the tenant published for marketplace listings. */
  blurb: string | null;
  /** Number of active federations the tenant already has. */
  activeFederations: number;
}

export async function listMarketplace(): Promise<MarketplaceEntry[]> {
  return api.get<MarketplaceEntry[]>('/federations/marketplace');
}
